import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { obtenerExpedientesPorCliente, actualizarCliente, obtenerPrioridad, actualizarPrioridad, obtenerNombreCliente, obtenerSeguido, cambiarSeguido, seguirExpediente } from '../services/seguimientoExpedientesService';
import { formatearCuil } from '../components/anses/ModalAgregarClienteAnses';
import ansesConexionService from '../services/ansesConexionService';
import { estiloEstado, Badge } from './SeguimientoExpedientesPage';
import LineaDeVidaExpediente from '../components/LineaDeVidaExpediente';
import ANSESLoginModal from '../components/anses/ANSESLoginModal';
import ModalHistorialAnses from '../components/anses/ModalHistorialAnses';
import { PRIORIDADES } from '../constants/prioridadAnses';
import PageHeader from '../components/ui/PageHeader';

const Dato = ({ etiqueta, valor }) => (
    <div style={{
        padding: '0.75rem', borderRadius: 8, background: '#f8fafc', border: '1px solid #f1f5f9',
        minWidth: 160, flex: '1 1 160px',
    }}>
        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', fontWeight: 700, marginBottom: 4 }}>
            {etiqueta}
        </div>
        <div style={{ color: '#0f172a', fontWeight: 500 }}>{valor || '—'}</div>
    </div>
);

const Card = ({ titulo, icono, accion, children, bodyStyle }) => (
    <div className="card shadow-sm mb-3" style={{ border: 'none', borderRadius: 12, overflow: 'hidden' }}>
        <div
            className="card-header d-flex align-items-center justify-content-between"
            style={{ background: 'var(--gradient-primary-light)', borderBottom: '1px solid #e5e7eb', padding: '0.9rem 1.25rem' }}
        >
            <h6 className="mb-0" style={{ fontWeight: 700, color: '#1e3a8a', display: 'flex', alignItems: 'center', gap: 8 }}>
                {icono && <i className={`bi ${icono}`} />}
                {titulo}
            </h6>
            {accion}
        </div>
        <div className="card-body" style={{ padding: '1.25rem', ...bodyStyle }}>
            {children}
        </div>
    </div>
);

// Subsección dentro de una card con fondo oscurecido — se separa en su propia caja blanca
// para que no quede plana contra el body de la card.
const Seccion = ({ icono, titulo, children }) => (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '1rem', height: '100%' }}>
        <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', fontWeight: 700, marginBottom: 10 }}>
            <i className={`bi ${icono} me-1`} />
            {titulo}
        </div>
        {children}
    </div>
);

const TramiteCard = ({ exp, onSeguir }) => {
    const e = estiloEstado(exp);
    return (
        <Card titulo={exp.tipoTramite} icono="bi-folder2-open" accion={<Badge exp={exp} />}>
            <div className="d-flex flex-wrap align-items-center gap-3" style={{ color: '#6B7280', marginBottom: 16, fontSize: 13 }}>
                <span>Expediente <span style={{ fontFamily: 'var(--font-family-mono)' }}>{exp.nroExpediente}</span></span>
                {/* Seguir el expediente (V266): si no, no avisa ni cuenta en Para aprobar. Misma marca que en el trámite. */}
                <label className="form-check mb-0 d-flex align-items-center gap-1" title="Si no se sigue, no avisa ni cuenta en Para aprobar">
                    <input type="checkbox" className="form-check-input mt-0" checked={exp.seguido !== false}
                        aria-label={`Seguir ${exp.nroExpediente}`}
                        onChange={(ev) => onSeguir(exp, ev.target.checked)} />
                    Seguir este expediente
                </label>
            </div>

            <div className="d-flex flex-wrap gap-3">
                <Dato etiqueta="Oficina" valor={exp.oficina} />
                <Dato etiqueta="Fecha de inicio" valor={exp.fechaInicio} />
                <Dato etiqueta="Fecha de resolución" valor={exp.fechaResolucion} />
                <Dato
                    etiqueta="Avance"
                    valor={
                        exp.estadoGeneral === 'EN_CURSO' && exp.totalEtapas
                            ? `Etapa ${exp.etapasCompletadas} de ${exp.totalEtapas}`
                            : '—'
                    }
                />
                <Dato etiqueta="Última actualización" valor={exp.fechaUltimaSync} />
            </div>

            {exp.mensaje && (
                <div
                    style={{
                        marginTop: 16,
                        background: '#f8fafc',
                        borderLeft: `3px solid ${e.fg}`,
                        padding: '0.75rem 1rem',
                        borderRadius: 6,
                        color: '#374151',
                        fontSize: 14,
                    }}
                >
                    {exp.mensaje}
                </div>
            )}

            <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#64748b', fontWeight: 700, marginBottom: 10 }}>
                    Línea de vida del trámite (ANSES)
                </div>
                <LineaDeVidaExpediente etapas={exp.etapas} />
            </div>
        </Card>
    );
};

// Clasifica un trámite en una de las 3 mini pestañas. Los CERRADO con resultado
// OTROS/RESUELTO/null (ej. "recaratulado / sin efecto") van a Rechazados, ya que
// tampoco son un resultado favorable.
const clasificarTramite = (exp) => {
    if (exp.estadoGeneral === 'EN_CURSO') return 'proceso';
    return exp.resultado === 'FAVORABLE' ? 'aceptados' : 'rechazados';
};

const TABS_TRAMITES = [
    { id: 'proceso', label: 'En proceso', icono: 'bi-hourglass-split' },
    { id: 'aceptados', label: 'Aceptados', icono: 'bi-check-circle' },
    { id: 'rechazados', label: 'Rechazados', icono: 'bi-x-circle' },
];

const ESTILO_CONEXION = {
    ACTIVE: { texto: 'Conectado', fg: '#166534', bg: '#dcfce7' },
    EXPIRED: { texto: 'Sesión expirada', fg: '#92400e', bg: '#fef3c7' },
    ERROR: { texto: 'Error de conexión', fg: '#991b1b', bg: '#fee2e2' },
    REQUIRES_LOGIN: { texto: 'No conectado', fg: '#475569', bg: '#f1f5f9' },
};

const BadgeConexion = ({ estado }) => {
    const e = ESTILO_CONEXION[estado] || ESTILO_CONEXION.REQUIRES_LOGIN;
    return (
        <span className="badge" style={{ background: e.bg, color: e.fg, padding: '0.5rem 0.9rem', borderRadius: 6, fontWeight: 600 }}>
            {e.texto}
        </span>
    );
};

const SeguimientoExpedienteDetalle = () => {
    const { cuil } = useParams();
    const navigate = useNavigate();
    const [expedientes, setExpedientes] = useState([]);
    const [nombreCliente, setNombreCliente] = useState(null);
    const [cargando, setCargando] = useState(true);
    const [estadoConexion, setEstadoConexion] = useState(null);
    const [modalAbierto, setModalAbierto] = useState(false);
    const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
    const [actualizando, setActualizando] = useState(false);
    const [sincronizandoTrasLogin, setSincronizandoTrasLogin] = useState(false);
    const [tabActiva, setTabActiva] = useState('proceso');
    const bannerSincronizandoRef = useRef(null);
    const [prioridad, setPrioridad] = useState('MEDIA');
    const [guardandoPrioridad, setGuardandoPrioridad] = useState(false);
    const [seguido, setSeguido] = useState(false);
    const [guardandoSeguido, setGuardandoSeguido] = useState(false);

    // silencioso=true: refresco en segundo plano (ej. el watcher de conexión de abajo), sin
    // tapar la página con el spinner de carga completo — solo el mount inicial lo usa en falso.
    const cargarExpedientes = useCallback(async (silencioso = false) => {
        if (!silencioso) setCargando(true);
        const data = await obtenerExpedientesPorCliente(cuil);
        setExpedientes(data);
        // Si los expedientes traen el nombre, úsarlo; si no (0 expedientes), pedir por separado
        if (data.length > 0 && data[0].clienteNombre) {
            setNombreCliente(data[0].clienteNombre);
        } else if (data.length === 0) {
            obtenerNombreCliente(cuil).then((n) => { if (n) setNombreCliente(n); });
        }
        if (!silencioso) setCargando(false);
    }, [cuil]);

    // Devuelve el estado recién leído (además de guardarlo) para que quien llama pueda actuar
    // sobre el valor fresco en el momento, sin depender del próximo render.
    const cargarEstadoConexion = useCallback(async () => {
        try {
            const data = await ansesConexionService.obtenerEstado(cuil);
            const estado = data?.estado || 'REQUIRES_LOGIN';
            setEstadoConexion(estado);
            return estado;
        } catch (e) {
            setEstadoConexion('REQUIRES_LOGIN');
            return 'REQUIRES_LOGIN';
        }
    }, [cuil]);

    // La prioridad guardada puede cambiar con el tiempo (el trámite se vuelve más
    // urgente, etc.) — hay que traer la real en vez de asumir MEDIA siempre.
    const cargarPrioridad = useCallback(async () => {
        const valor = await obtenerPrioridad(cuil);
        setPrioridad(valor);
    }, [cuil]);

    // Si el usuario actual sigue a este cliente (le llegan sus avisos de ANSES).
    const cargarSeguido = useCallback(async () => {
        setSeguido(await obtenerSeguido(cuil));
    }, [cuil]);

    useEffect(() => {
        cargarExpedientes();
        cargarEstadoConexion();
        cargarPrioridad();
        cargarSeguido();
        return () => {
            if (bannerSincronizandoRef.current) clearTimeout(bannerSincronizandoRef.current);
        };
    }, [cargarExpedientes, cargarEstadoConexion, cargarPrioridad, cargarSeguido]);

    // Sigue chequeando el estado de la conexión cada 15s mientras no esté ACTIVE — cubre tanto
    // "recién conectado, esperando la primera sync" como "sesión EXPIRED/ERROR reconectándose
    // sola vía el keep-alive de servicioAnses" — e independiente de si el modal de conexión
    // sigue abierto o no: el modal mismo invita a cerrarlo y seguir trabajando ("la conexión
    // sigue intentando sola en segundo plano"), pero antes nada en esta página se enteraba de
    // que había terminado si lo cerrabas antes de que el modal detectara el éxito. Apenas pasa
    // a ACTIVE, refresca los expedientes en silencio (sin tapar la página con el spinner).
    useEffect(() => {
        if (estadoConexion === null || estadoConexion === 'ACTIVE') return undefined;
        const interval = setInterval(async () => {
            const estado = await cargarEstadoConexion();
            if (estado === 'ACTIVE') {
                if (bannerSincronizandoRef.current) { clearTimeout(bannerSincronizandoRef.current); bannerSincronizandoRef.current = null; }
                setSincronizandoTrasLogin(false);
                cargarExpedientes(true);
            }
        }, 15000);
        return () => clearInterval(interval);
    }, [estadoConexion, cargarEstadoConexion, cargarExpedientes]);

    const volver = () => navigate('/herramientas/seguimiento-expedientes');

    const handleConectado = useCallback(() => {
        setModalAbierto(false);
        setSincronizandoTrasLogin(true);
        cargarEstadoConexion();
        // El banner de "sincronizando" no se queda pegado para siempre si termina en
        // ERROR/REQUIRES_LOGIN (necesita VNC manual) — el watcher de arriba sigue chequeando
        // solo igual, por si se recupera después sin que haga falta volver a esta página.
        if (bannerSincronizandoRef.current) clearTimeout(bannerSincronizandoRef.current);
        bannerSincronizandoRef.current = setTimeout(() => setSincronizandoTrasLogin(false), 2 * 60 * 1000);
    }, [cargarEstadoConexion]);

    // La prioridad es un valor único y compartido: cualquier usuario que la cambie la
    // actualiza para todos.
    const handleCambiarPrioridad = async (nuevaPrioridad) => {
        setPrioridad(nuevaPrioridad);
        setGuardandoPrioridad(true);
        try {
            await actualizarPrioridad(cuil, nuevaPrioridad);
        } finally {
            setGuardandoPrioridad(false);
        }
    };

    // Seguir un expediente puntual: es una marca compartida (no personal), la misma del trámite.
    const handleSeguirExpediente = async (exp, seguir) => {
        setExpedientes((prev) => prev.map((x) => (x.nroExpediente === exp.nroExpediente ? { ...x, seguido: seguir } : x)));
        try {
            await seguirExpediente(cuil, exp.nroExpediente, seguir);
        } catch (e) {
            cargarExpedientes(true); // revertir recargando si falló
        }
    };

    // Seguir es personal: no cambia lo que recibe nadie más.
    const handleToggleSeguido = async () => {
        const nuevo = !seguido;
        setSeguido(nuevo);
        setGuardandoSeguido(true);
        try {
            await cambiarSeguido(cuil, nuevo);
        } catch (e) {
            cargarSeguido(); // revertir recargando si falló
        } finally {
            setGuardandoSeguido(false);
        }
    };

    const handleActualizar = async () => {
        setActualizando(true);
        try {
            const data = await actualizarCliente(cuil);
            if (data.length) setExpedientes(data);
        } finally {
            setActualizando(false);
        }
    };

    const tramitesPorTab = useMemo(() => {
        const grupos = { proceso: [], aceptados: [], rechazados: [] };
        expedientes.forEach((exp) => grupos[clasificarTramite(exp)].push(exp));
        return grupos;
    }, [expedientes]);

    if (cargando) {
        return (
            <div className="container-fluid mt-4 text-center my-5">
                <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} />
                <p className="mt-3 fw-semibold" style={{ color: '#6B7280' }}>Cargando expedientes…</p>
            </div>
        );
    }

    const nombreMostrado = nombreCliente || expedientes[0]?.clienteNombre || cuil;

    return (
        <div className="area-anses container-fluid mt-4" style={{ maxWidth: 900 }}>
            <PageHeader
                variante="banner"
                icono="person-fill"
                titulo={nombreMostrado}
                descripcion={`CUIL ${formatearCuil(cuil)} · ${expedientes.length} trámite${expedientes.length !== 1 ? 's' : ''}`}
                accion={
                    <button
                        className="btn btn-light d-flex align-items-center gap-2"
                        onClick={volver}
                        style={{ borderRadius: 8, padding: '0.5rem 1.1rem', fontWeight: 600 }}
                    >
                        <i className="bi bi-arrow-left" />
                        Volver al listado
                    </button>
                }
            />

            {/* Conexión con Mi ANSES — siempre visible, incluso sin expedientes sincronizados aún */}
            <Card
                titulo="Conexión con Mi ANSES"
                icono="bi-link-45deg"
                accion={<BadgeConexion estado={estadoConexion} />}
                bodyStyle={{ background: '#f8fafc' }}
            >
                <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 14 }}>
                    Vinculá la cuenta de Mi ANSES del cliente para sincronizar sus trámites automáticamente.
                </p>
                <div className="d-flex gap-2 mb-4">
                    <button
                        type="button"
                        className="btn btn-primary d-flex align-items-center gap-2"
                        onClick={() => setModalAbierto(true)}
                        style={{ borderRadius: 8, padding: '0.5rem 1.2rem', fontWeight: 500 }}
                    >
                        <i className="bi bi-plug" />
                        Conectar Mi ANSES
                    </button>
                    {estadoConexion === 'ACTIVE' && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary d-flex align-items-center gap-2"
                            onClick={handleActualizar}
                            disabled={actualizando}
                            style={{ borderRadius: 8, padding: '0.5rem 1.2rem', fontWeight: 500 }}
                        >
                            <i className="bi bi-arrow-clockwise" />
                            {actualizando ? 'Actualizando…' : 'Actualizar'}
                        </button>
                    )}
                    <button
                        type="button"
                        className="btn btn-outline-secondary d-flex align-items-center gap-2"
                        onClick={() => setModalHistorialAbierto(true)}
                        style={{ borderRadius: 8, padding: '0.5rem 1.2rem', fontWeight: 500 }}
                    >
                        <i className="bi bi-clock-history" />
                        Ver historial
                    </button>
                </div>

                {/* Prioridad y Seguir, lado a lado — mismas alturas, cada una en su caja */}
                <div className="row g-3">
                    <div className="col-md-6">
                        <Seccion icono="bi-speedometer2" titulo="Prioridad de seguimiento">
                            <div className="d-flex gap-2">
                                {PRIORIDADES.map(({ valor, label }) => (
                                    <button
                                        key={valor}
                                        type="button"
                                        onClick={() => handleCambiarPrioridad(valor)}
                                        disabled={guardandoPrioridad}
                                        style={{
                                            padding: '0.35rem 0.9rem',
                                            borderRadius: 6,
                                            border: `2px solid ${prioridad === valor ? '#3b82f6' : '#d1d5db'}`,
                                            background: prioridad === valor ? '#eff6ff' : '#fff',
                                            color: prioridad === valor ? '#1e40af' : '#475569',
                                            fontWeight: prioridad === valor ? 700 : 400,
                                            fontSize: 13,
                                        }}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                                Alta revisa más seguido, Baja espacía las revisiones. Es un valor único y compartido:
                                cualquier usuario que la cambie la actualiza para todos.
                            </div>
                        </Seccion>
                    </div>

                    <div className="col-md-6">
                        <Seccion icono="bi-star" titulo="Avisos de este cliente">
                            <button
                                type="button"
                                className={`btn btn-sm ${seguido ? 'btn-warning' : 'btn-outline-secondary'}`}
                                onClick={handleToggleSeguido}
                                disabled={guardandoSeguido}
                                aria-pressed={seguido}
                                style={{ borderRadius: 6, fontSize: 13 }}
                            >
                                <i className={`bi ${seguido ? 'bi-star-fill' : 'bi-star'} me-1`} />
                                {seguido ? 'Siguiendo' : 'Seguir'}
                            </button>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                                Si lo seguís, te llegan los avisos de todos sus expedientes (Favorable, Desfavorable,
                                Citado, Desaparecido) por los canales que tengas prendidos en Para aprobar → Mis avisos.
                                Solo te afecta a vos.
                            </div>
                        </Seccion>
                    </div>
                </div>
            </Card>

            {sincronizandoTrasLogin && (
                <div className="alert d-flex align-items-center gap-3 mb-3" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10 }}>
                    <div className="spinner-border spinner-border-sm text-primary flex-shrink-0" role="status" />
                    <div>
                        <strong style={{ color: '#1e40af' }}>Sincronizando expedientes…</strong>
                        <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 2 }}>
                            Se está consultando ANSES. Puede tomar hasta 2 minutos la primera vez.
                        </div>
                    </div>
                </div>
            )}

            {expedientes.length === 0 ? (
                <div className="card shadow-sm" style={{ border: 'none', borderRadius: 12 }}>
                    <div className="text-center py-5">
                        <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#cbd5e1' }} />
                        <p className="mt-3" style={{ color: '#64748b', fontSize: '1.1rem' }}>
                            {sincronizandoTrasLogin
                                ? 'Esperando la primera sincronización con ANSES…'
                                : 'Todavía no hay expedientes sincronizados para este cliente.'}
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    <div className="btn-group mb-3" role="group">
                        {TABS_TRAMITES.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                className={`btn ${tabActiva === tab.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setTabActiva(tab.id)}
                            >
                                <i className={`bi ${tab.icono} me-2`} />
                                {tab.label} ({tramitesPorTab[tab.id].length})
                            </button>
                        ))}
                    </div>

                    {tramitesPorTab[tabActiva].length === 0 ? (
                        <div className="card shadow-sm" style={{ border: 'none', borderRadius: 12 }}>
                            <div className="text-center py-4" style={{ color: '#64748b' }}>
                                No hay trámites en esta categoría.
                            </div>
                        </div>
                    ) : (
                        tramitesPorTab[tabActiva].map((exp) => <TramiteCard key={exp.nroExpediente} exp={exp} onSeguir={handleSeguirExpediente} />)
                    )}
                </>
            )}

            <ANSESLoginModal
                open={modalAbierto}
                cuil={cuil}
                nombre={nombreMostrado !== cuil ? nombreMostrado : undefined}
                onClose={() => setModalAbierto(false)}
                onConectado={handleConectado}
            />

            <ModalHistorialAnses
                open={modalHistorialAbierto}
                cuil={cuil}
                onClose={() => setModalHistorialAbierto(false)}
            />
        </div>
    );
};

export default SeguimientoExpedienteDetalle;
